import { useRef, useState, type KeyboardEvent } from "react";
import type { LucideIcon } from "lucide-react";
import { ResponsiveImage } from "@/components/responsive-image";
import type { RasterImageSet } from "@/generated/raster-images";

export type FinanceAccordionPanel = {
  Icon: LucideIcon;
  title: string;
  body: string;
  image?: RasterImageSet;
  imageAlt?: string;
  tone: "primary" | "accent";
};

interface FinanceOptionsAccordionProps {
  panels: FinanceAccordionPanel[];
  ariaLabel?: string;
}

export function FinanceOptionsAccordion({
  panels,
  ariaLabel = "How finance works",
}: FinanceOptionsAccordionProps) {
  const [open, setOpen] = useState(0);
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKey(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = (i + 1) % panels.length;
      setOpen(next);
      btnRefs.current[next]?.focus();
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prev = (i - 1 + panels.length) % panels.length;
      setOpen(prev);
      btnRefs.current[prev]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      setOpen(0);
      btnRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      const last = panels.length - 1;
      setOpen(last);
      btnRefs.current[last]?.focus();
    }
  }

  return (
    <div className="space-y-2 md:space-y-3" role="tablist" aria-label={ariaLabel}>
      {panels.map((p, i) => {
        const isOpen = open === i;
        const overlay = p.tone === "primary" ? "bg-primary" : "bg-accent";
        return (
          <div
            key={p.title}
            className={`overflow-hidden rounded-lg md:rounded-xl border transition-colors duration-300 ${
              isOpen ? "border-border" : "border-transparent"
            }`}
          >
            <button
              ref={(el) => {
                btnRefs.current[i] = el;
              }}
              role="tab"
              aria-expanded={isOpen}
              aria-selected={isOpen}
              aria-controls={`fin-panel-${i}`}
              id={`fin-tab-${i}`}
              onClick={() => setOpen(i)}
              onKeyDown={(e) => onKey(e, i)}
              className="group relative w-full h-16 md:h-20 overflow-hidden text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:z-10"
            >
              {p.image ? (
                <div className="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-105">
                  <ResponsiveImage
                    image={p.image}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div
                  className={`absolute inset-0 ${overlay} opacity-100`}
                  aria-hidden
                />
              )}
              <div
                className={`absolute inset-0 ${overlay} transition-opacity duration-300 ease-out ${
                  isOpen ? "opacity-[0.82]" : "opacity-[0.72] group-hover:opacity-[0.6]"
                }`}
                aria-hidden
              />
              <div className="relative flex items-center gap-3 md:gap-4 px-4 md:px-6 h-full text-primary-foreground">
                <span className="inline-flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm shrink-0 transition-transform duration-300 ease-out group-hover:scale-110">
                  <p.Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden />
                </span>
                <span className="font-medium text-sm md:text-base">{p.title}</span>
                <span
                  className={`ml-auto text-xs md:text-sm opacity-80 transition-transform duration-300 ease-out ${
                    isOpen ? "rotate-90" : "group-hover:translate-x-0.5"
                  }`}
                  aria-hidden
                >
                  ▸
                </span>
              </div>
            </button>
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-out"
              style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <div
                  id={`fin-panel-${i}`}
                  role="tabpanel"
                  aria-labelledby={`fin-tab-${i}`}
                  aria-hidden={!isOpen}
                  className="bg-background px-4 md:px-6 py-4 md:py-5"
                >
                  <p className="text-sm md:text-base text-foreground/85 leading-relaxed">{p.body}</p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
